import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";

import FormRichTextField from "../../components/formFields/FormRichTextField";

import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  createService,
  getServiceByID,
  ServiceImageUpload,
  updateService,
} from "./services/services.services";
import { clearServiceDetail } from "./servicesSlice";
import { slugify } from "../../_helper/helper";

const CreateService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ServiceID = location.state?.ServiceID;
  const ServiceDetail = useAppSelector((state) => state.service.ServiceDetail);
  const loading = useAppSelector((state) => state.service.loading);

  useEffect(() => {
    if (ServiceID) {
      dispatch(getServiceByID(ServiceID));
    }
    return () => {
      dispatch(clearServiceDetail());
    };
  }, [ServiceID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ServiceName: values?.ServiceName,
      URL: values?.URL,
      LongDescription: values?.LongDescription,
      ShortDescription: values?.ShortDescription,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage &&
      typeof values?.MainImage !== "string" &&
      Array.isArray(values?.MainImage) &&
      values?.MainImage?.length > 0
        ? values.MainImage[0].getRawFile()
        : "";

    const BannerImage =
      values?.BannerImage &&
      typeof values?.BannerImage !== "string" &&
      Array.isArray(values?.BannerImage) &&
      values?.BannerImage?.length > 0
        ? values.BannerImage[0].getRawFile()
        : "";
    const Brochure =
      values?.Brochure &&
      typeof values?.Brochure !== "string" &&
      Array.isArray(values?.Brochure) &&
      values?.Brochure?.length > 0
        ? values.Brochure[0].getRawFile()
        : "";

    try {
      if (ServiceID) {
        const editPayload = {
          ...formData,
          ID: ServiceID,
        };

        const response = await dispatch(updateService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage &&
              typeof values?.MainImage !== "string" &&
              Array.isArray(values?.MainImage) &&
              values?.MainImage?.length > 0) ||
            (values?.BannerImage &&
              typeof values?.BannerImage !== "string" &&
              Array.isArray(values?.BannerImage) &&
              values?.BannerImage?.length > 0) ||
            (values?.Brochure &&
              typeof values?.Brochure !== "string" &&
              Array.isArray(values?.Brochure) &&
              values?.Brochure?.length > 0)
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("Brochure", Brochure);
            ImageData.append("ID", ServiceID);
            const imageResponse = await dispatch(ServiceImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/service");
            } else {
              navigate("/service");
            }
          } else {
            navigate("/service");
          }
        }
      } else {
        const response = await dispatch(createService(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (response?.payload?.Data?.ID &&
              values?.MainImage &&
              typeof values?.MainImage !== "string" &&
              Array.isArray(values?.MainImage) &&
              values?.MainImage?.length > 0) ||
            (values?.BannerImage &&
              typeof values?.BannerImage !== "string" &&
              Array.isArray(values?.BannerImage) &&
              values?.BannerImage?.length > 0) ||
            (values?.Brochure &&
              typeof values?.Brochure !== "string" &&
              Array.isArray(values?.Brochure) &&
              values?.Brochure?.length > 0)
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("Brochure", Brochure);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(ServiceImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/service");
            } else {
              navigate("/service");
            }
          } else {
            navigate("/service");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={ServiceDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ServiceID ? "Update Service" : "Create Service"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ServiceName"
                      label="Service Name"
                      placeholder="i.e. Commercial Solutions"
                      component={FormTextField}
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="i.e.commercial-solutions"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={2}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      style={{ height: 250 }}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={2}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      component={FormRichTextField}
                      style={{ height: 250 }}
                    />
                  </GridLayoutItem>

                  {/* <GridLayoutItem></GridLayoutItem> */}
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Image"
                      component={FormImageUploadMorden}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Service Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="service-bannner-image"
                      component={FormImageUploadMorden}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      justifyContent="start"
                      name={`Brochure`}
                      label="Brochure"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      alternateName="service-brochure"
                      acceptFileTypesMessage="Only PDF and Word (DOC/DOCX) files are accepted."
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{
                      marginTop: 20,
                    }}
                  >
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ServiceID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"secondary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/service")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateService;
